"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import type { ExperienceEntry } from "@/constants";
import { cn } from "@/utils";
import { MotionValue, useTransform, motion, AnimatePresence, useMotionValueEvent } from "framer-motion";
import { ExperienceOrbitCard } from "@/components/sections/experience/ExperienceOrbitCard";

import styles from "@/ui/styles/shared.module.css";

interface RadialOrbitalTimelineProps {
  timelineData: ExperienceEntry[];
  revealProgress: MotionValue<number>;
}

export function RadialOrbitalTimeline({
  timelineData,
  revealProgress,
}: RadialOrbitalTimelineProps) {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {}
  );
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [autoRotate, setAutoRotate] = useState<boolean>(true);
  const [centerOffset, setCenterOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [orbitOffset, setOrbitOffset] = useState<number>(0);
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [shouldUseOverlay, setShouldUseOverlay] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const orbitRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const planetTop = useTransform(revealProgress, [0, 0.1], [0, 0], { clamp: true });

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current || e.target === orbitRef.current) {
      setExpandedItems({});
      setActiveNodeId(null);
      setAutoRotate(true);
      setOrbitOffset(0);
    }
  };

  const toggleItem = (id: string) => {
    setExpandedItems((prev) => {
      const isCurrentlyExpanded = prev[id];

      // If clicking the same item, close it
      if (isCurrentlyExpanded) {
        setActiveNodeId(null);
        setAutoRotate(true);
        setOrbitOffset(0);
        return {};
      }

      // Otherwise, close any previously expanded item and open the new one
      setActiveNodeId(id);
      setAutoRotate(false);
      // Only offset orbit when not using overlay layout
      if (!shouldUseOverlay) {
        setOrbitOffset(-250);
      }
      centerViewOnNode(id);

      return { [id]: true };
    });
  };

  useEffect(() => {
    let rotationTimer: NodeJS.Timeout;

    if (autoRotate) {
      rotationTimer = setInterval(() => {
        setRotationAngle((prev) => {
          const newAngle = (prev + 0.3) % 360;
          return Number(newAngle.toFixed(3));
        });
      }, 50);
    }

    return () => {
      if (rotationTimer) {
        clearInterval(rotationTimer);
      }
    };
  }, [autoRotate]);

  // Ensure component is mounted (client-side only)
  useEffect(() => {
    setIsMounted(true);

    // Check if we should use overlay layout (mobile or small height screens)
    const checkViewport = () => {
      // Use overlay if width < 812px OR height < 700px (for laptops with small height)
      const shouldOverlay = window.innerWidth < 812 || window.innerHeight < 700;

      setShouldUseOverlay(shouldOverlay);
    };

    checkViewport();
    window.addEventListener("resize", checkViewport);

    return () => {
      window.removeEventListener("resize", checkViewport);
    };
  }, []);

  // Close expanded card when timeline shrinks (revealProgress decreases)
  const previousProgressRef = useRef<number>(0);
  
  useMotionValueEvent(revealProgress, "change", (latest) => {
    if (!activeNodeId) {
      previousProgressRef.current = latest;
      return;
    }

    // If progress is decreasing (timeline is shrinking), close the card
    if (latest < previousProgressRef.current) {
      setExpandedItems({});
      setActiveNodeId(null);
      setAutoRotate(true);
      setOrbitOffset(0);
    }

    previousProgressRef.current = latest;
  });

  const centerViewOnNode = (nodeId: string) => {
    if (!nodeRefs.current[parseInt(nodeId)]) return;

    const nodeIndex = timelineData.findIndex((item) => item.id === nodeId);
    const totalNodes = timelineData.length;
    const targetAngle = (nodeIndex / totalNodes) * 360;

    setRotationAngle(270 - targetAngle);
  };

  const calculateNodePosition = (index: number, total: number) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radius = 200;
    const radian = (angle * Math.PI) / 180;

    const x = Math.round((radius * Math.cos(radian) + centerOffset.x) * 100) / 100;
    const y = Math.round((radius * Math.sin(radian) + centerOffset.y) * 100) / 100;

    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const opacity = Math.max(
      0.4,
      Math.min(1, 0.4 + 0.6 * ((1 + Math.sin(radian)) / 2))
    );

    return { x, y, angle, zIndex, opacity };
  };

  const expandedExperience = activeNodeId
    ? timelineData.find((item) => item.id === activeNodeId)
    : null;

  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center"
      ref={containerRef}
      onClick={handleContainerClick}
    >
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Orbit Container - animates left when expanded */}
        <motion.div
          className="absolute w-full h-full flex items-center justify-center"
          ref={orbitRef}
          animate={{
            x: orbitOffset,
            transition: { duration: 0.6, ease: "easeInOut" },
          }}
          style={{
            perspective: "1000px",
            transform: `translate(${centerOffset.x}px, ${centerOffset.y}px)`,
          }}
        >
          <motion.div
            className={styles.experienceOrbitShell}
            style={{ top: planetTop }}
            aria-hidden="true"
          >
            <div className={styles.orbitCore} />
          </motion.div>

          {isMounted && timelineData.map((item, index) => {
            const position = calculateNodePosition(index, timelineData.length);
            const isExpanded = expandedItems[item.id];

            const nodeStyle = {
              transform: `translate(${position.x}px, ${position.y}px)`,
              zIndex: isExpanded ? 200 : position.zIndex,
              opacity: isExpanded ? 1 : 0.5,
            };

            return (
              <div
                key={item.id}
                ref={(el: HTMLDivElement | null) => { nodeRefs.current[item.id] = el; }}
                className="absolute transition-all duration-700 cursor-pointer flex flex-col items-center justify-center"
                style={nodeStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItem(item.id);
                }}
              >

                <div
                  className={cn(
                    "flex items-center justify-center overflow-hidden rounded-full",
                    isExpanded ?
                      "bg-white text-black border-white shadow-lg shadow-white/30 scale-150"
                      :
                      "bg-black text-white border-white/40",
                    "transition-all duration-300 transform",
                  )}
                  style={{
                    width: "60px",
                    height: "60px",
                    backgroundColor: item.iconBg,
                  }}
                >
                  <div
                    className="relative w-full h-full rounded-full"
                    style={{ backgroundColor: item.iconBg }}
                  >
                    <Image
                      src={item.icon}
                      alt={item.company_name}
                      fill
                      sizes="40px"
                      className={cn("object-contain p-2", item.iconBg)}
                    />
                  </div>
                </div>

                <div
                  className={cn(
                    "absolute text-xs font-semibold tracking-wider transition-all duration-300 text-center",
                    isExpanded ? "text-white scale-125" : "text-white/70",
                  )}
                  style={{
                    width: "130px",
                    top: "70px",
                  }}
                >
                  {item.title}
                </div>

              </div>
            );
          })}
        </motion.div>

        {/* Backdrop overlay for compact viewports */}
        <AnimatePresence>
          {expandedExperience && shouldUseOverlay && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedItems({});
                setActiveNodeId(null);
                setAutoRotate(true);
                setOrbitOffset(0);
              }}
            />
          )}
        </AnimatePresence>

        {/* Experience Card - appears on the right when expanded (desktop) or centered overlay (compact viewports) */}
        <AnimatePresence>
          {expandedExperience && (
            <motion.div
              initial={{
                opacity: 0,
                x: shouldUseOverlay ? 0 : 50,
                y: "-50%",
                scale: 0.9
              }}
              animate={{
                opacity: 1,
                x: 0,
                y: "-50%",
                scale: 1
              }}
              exit={{
                opacity: 0,
                x: shouldUseOverlay ? 0 : 150,
                y: "-50%",
                scale: 0.9
              }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className={cn(
                "absolute z-50",
                shouldUseOverlay
                  ? "left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[calc(100%-2rem)]"
                  : "right-12 left-auto w-auto max-w-none"
              )}
              style={{
                top: "50%",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <ExperienceOrbitCard
                experience={expandedExperience}
                onClose={() => {
                  setExpandedItems({});
                  setActiveNodeId(null);
                  setAutoRotate(true);
                  setOrbitOffset(0);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
